import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Cookie } from "lucide-react";

const KEY = "si-cookie-consent";

export function openCookiePreferences() {
    window.dispatchEvent(new CustomEvent("si-open-cookie-preferences"));
}

function readConsent() {
    try {
        return JSON.parse(localStorage.getItem(KEY));
    } catch {
        return null;
    }
}

export default function CookieBanner() {
    const [open, setOpen] = useState(false);
    const [showPrefs, setShowPrefs] = useState(false);
    const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

    useEffect(() => {
        const saved = readConsent();
        if (saved) {
            setPrefs({ analytics: !!saved.analytics, marketing: !!saved.marketing });
            window.__cookieConsent = saved;
        } else {
            const t = setTimeout(() => setOpen(true), 1800);
            return () => clearTimeout(t);
        }
    }, []);

    useEffect(() => {
        const openPrefs = () => {
            setShowPrefs(true);
            setOpen(true);
        };
        window.addEventListener("si-open-cookie-preferences", openPrefs);
        return () => window.removeEventListener("si-open-cookie-preferences", openPrefs);
    }, []);

    const save = (next) => {
        const value = { ...next, ts: new Date().toISOString() };
        try {
            localStorage.setItem(KEY, JSON.stringify(value));
        } catch {}
        window.__cookieConsent = value;
        setPrefs(next);
        setOpen(false);
        setShowPrefs(false);
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    key="cookie-banner"
                    initial={{ opacity: 0, y: 40, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 40, scale: 0.97 }}
                    transition={{ type: "spring", stiffness: 240, damping: 26 }}
                    data-testid="cookie-banner"
                    className="fixed inset-x-3 bottom-[92px] z-[70] rounded-3xl border border-ink/10 bg-white p-6 shadow-[0_30px_70px_-20px_rgba(11,18,32,0.4)] md:inset-x-auto md:bottom-6 md:left-6 md:max-w-md"
                >
                    <div className="flex items-center gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-telegram-light text-telegram-deep">
                            <Cookie className="h-5 w-5" />
                        </span>
                        <h3 className="font-display text-lg font-semibold tracking-tight text-ink">
                            Rispettiamo la tua privacy
                        </h3>
                    </div>

                    <p className="mt-3 text-[13.5px] leading-relaxed text-slate-500">
                        Il sito usa solo cookie tecnici necessari al suo funzionamento. Gli eventuali
                        cookie analitici e di marketing (Google Analytics, Meta Pixel) verranno attivati
                        solo con il tuo consenso.{" "}
                        <a href="/cookie-policy" className="font-semibold text-telegram-deep hover:underline">
                            Cookie Policy
                        </a>
                    </p>

                    {showPrefs && (
                        <div className="mt-4 space-y-2.5 rounded-2xl bg-paper p-4" data-testid="cookie-prefs-panel">
                            <label className="flex items-center gap-3 text-sm font-medium text-ink">
                                <input type="checkbox" checked disabled className="h-4 w-4 accent-telegram" />
                                Cookie tecnici (sempre attivi)
                            </label>
                            <label className="flex items-center gap-3 text-sm font-medium text-ink">
                                <input
                                    type="checkbox"
                                    checked={prefs.analytics}
                                    onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })}
                                    data-testid="cookie-pref-analytics"
                                    className="h-4 w-4 accent-telegram"
                                />
                                Cookie analitici (Google Analytics / GTM)
                            </label>
                            <label className="flex items-center gap-3 text-sm font-medium text-ink">
                                <input
                                    type="checkbox"
                                    checked={prefs.marketing}
                                    onChange={(e) => setPrefs({ ...prefs, marketing: e.target.checked })}
                                    data-testid="cookie-pref-marketing"
                                    className="h-4 w-4 accent-telegram"
                                />
                                Cookie di marketing (Meta Pixel)
                            </label>
                        </div>
                    )}

                    <div className="mt-5 flex flex-col gap-2 sm:flex-row">
                        {showPrefs ? (
                            <button
                                onClick={() => save(prefs)}
                                data-testid="cookie-save-prefs"
                                className="inline-flex flex-1 items-center justify-center rounded-full bg-ink px-5 py-2.5 text-[13px] font-bold text-white transition-colors hover:bg-ink-700"
                            >
                                Salva preferenze
                            </button>
                        ) : (
                            <button
                                onClick={() => setShowPrefs(true)}
                                data-testid="cookie-prefs-toggle"
                                className="inline-flex flex-1 items-center justify-center rounded-full bg-paper px-5 py-2.5 text-[13px] font-bold text-ink ring-1 ring-ink/10 transition-colors hover:ring-telegram"
                            >
                                Preferenze
                            </button>
                        )}
                        <button
                            onClick={() => save({ analytics: false, marketing: false })}
                            data-testid="cookie-necessary"
                            className="inline-flex flex-1 items-center justify-center rounded-full bg-paper px-5 py-2.5 text-[13px] font-bold text-ink ring-1 ring-ink/10 transition-colors hover:ring-telegram"
                        >
                            Solo necessari
                        </button>
                        <button
                            onClick={() => save({ analytics: true, marketing: true })}
                            data-testid="cookie-accept-all"
                            className="inline-flex flex-1 items-center justify-center rounded-full bg-telegram px-5 py-2.5 text-[13px] font-bold text-white transition-colors hover:bg-telegram-deep"
                        >
                            Accetta tutti
                        </button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
